import React from "react";
import { Control, useWatch } from "react-hook-form";
import {
  CheckboxController,
  Divider,
  GroupedController,
  NumberController,
  ReactSelectController,
  RichController,
  SwitchController,
  Text,
} from "@pushpress/shared-components";
import {
  Option,
  OptionGroup,
} from "@pushpress/shared-components/build/components/inputs/RichInput/RichInput.types";
import { AppointmentTypeForm } from "./AppointmentTypeDrawer.types";
import { smartTextOptions, timingOptions } from "./AppointmentTypeDrawer.utils";

interface AppointmentTypeNotifications {
  control: Control<AppointmentTypeForm>;
}

function AppointmentTypeSettings(props: AppointmentTypeNotifications) {
  const { control } = props;

  const [sendEmailNotification, sendEmailReminder, sendTextReminder] = useWatch(
    {
      control,
      name: ["sendEmailNotification", "sendEmailReminder", "sendTextReminder"],
    },
  );

  const printSmartText = (
    value: string,
    option?: Option,
    optionGroup?: OptionGroup,
  ) => {
    const groupValue = optionGroup?.label || "";
    const optionValue = option?.label || "";
    if (!groupValue && !optionValue) {
      return value;
    }
    return [groupValue, optionValue].join(" ").trim();
  };

  return (
    <div className="p-4">
      <Text variant="heading-5" color="wolf-800" weight="600">
        Booking confirmation
      </Text>
      <Text variant="body-md" color="wolf-600" className="mb-3">
        Notify a client when an appointment is booked
      </Text>
      <SwitchController
        control={control}
        name="sendEmailNotification"
        label="Email"
        className="mt-3"
      />
      {sendEmailNotification && (
        <>
          <RichController
            control={control}
            name="emailNotificationSubject"
            label="Subject"
            variant="single-line"
            required
            valueType="text"
            smartTextOptions={smartTextOptions}
            printSmartText={printSmartText}
            className="mt-3"
          />
          <RichController
            control={control}
            name="message"
            label="Message"
            required
            minHeight="160px"
            valueType="html"
            smartTextOptions={smartTextOptions}
            printSmartText={printSmartText}
            multiLineToolbarPosition="bottom"
          />
          <CheckboxController
            control={control}
            name="notifyStaffMember"
            label="Bcc staff"
          />
        </>
      )}
      <Divider vPadding={24} marginRight={-28} />

      <Text variant="heading-5" color="wolf-800" weight="600">
        Reminders
      </Text>
      <Text variant="body-md" color="wolf-600" className="mb-3">
        Notify a client when they have an upcoming appointment
      </Text>

      <SwitchController
        control={control}
        name="sendEmailReminder"
        label="Email"
        className="mt-3"
      />
      {sendEmailReminder && (
        <>
          <GroupedController
            label="Timing"
            required
            flexItems={["0 0 92px", "1"]}
            className="mt-3"
          >
            <NumberController
              control={control}
              name="emailReminderTimingValue"
              label="Value"
              min={1}
              autoComplete="off"
            />
            <ReactSelectController
              control={control}
              name="emailReminderTimingUnit"
              label="Unit"
              options={timingOptions}
            />
          </GroupedController>
          <RichController
            control={control}
            name="emailReminderSubject"
            label="Subject"
            variant="single-line"
            required
            valueType="text"
            smartTextOptions={smartTextOptions}
            printSmartText={printSmartText}
          />
          <RichController
            control={control}
            name="emailReminderMessage"
            label="Message"
            required
            minHeight="160px"
            valueType="html"
            smartTextOptions={smartTextOptions}
            printSmartText={printSmartText}
            multiLineToolbarPosition="bottom"
          />
        </>
      )}
      <Divider vPadding={24} />

      <SwitchController
        control={control}
        name="sendTextReminder"
        label="Text message"
      />
      {sendTextReminder && (
        <>
          <GroupedController
            label="Timing"
            required
            flexItems={["0 0 92px", "1"]}
            className="mt-3"
          >
            <NumberController
              control={control}
              name="textReminderTimingValue"
              label="Value"
              min={1}
            />
            <ReactSelectController
              control={control}
              name="textReminderTimingUnit"
              label="Unit"
              options={timingOptions}
            />
          </GroupedController>
          <RichController
            control={control}
            name="textReminderMessage"
            label="Message"
            required
            minHeight="160px"
            valueType="html"
            smartTextOptions={smartTextOptions}
            printSmartText={printSmartText}
            features={["smartText", "link"]}
            multiLineToolbarPosition="bottom"
          />
        </>
      )}
    </div>
  );
}

export default AppointmentTypeSettings;
